<template>
  <div>
    <v-fab-transition v-if="isModification">
      <v-btn v-hotkey="'ctrl+s'" color="green" @click="save" icon size="large" class="fab-fixed-bottom-right">
        <v-icon size="large">mdi-content-save-outline</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-container>
      <v-row>
        <v-col>
          <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error">
            {{ errorMessage }}
          </v-alert>
          <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning">
            Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-switch v-model="section.topo_info" label="Activer la section Topographie"></v-switch>
          <small class="text-medium-emphasis"> (L'activation de la section sera sauvegardée automatiquement)</small>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="uploadWaiting">
          <div class="text-center">
            <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
            <p class="text-blue">Veuillez patienter durant le téléchargement.</p>
          </div>
        </v-col>
        <v-col v-if="!uploadWaiting">
          <FileDrop :files="photosDropzone" accept=".jpg,.JPG" label multiple></FileDrop>
        </v-col>
      </v-row>
      <v-row v-if="photosDropzone.length !== 0">
        <v-col class="d-flex justify-center">
          <v-btn class="primary" @click="uploadPhotos()" :disabled="uploadWaiting" :loading="uploadWaiting">
            <v-icon start>mdi-cloud-upload</v-icon>
            Télécharger les images
          </v-btn>
        </v-col>
      </v-row>
      <v-form ref="sectionTopos" v-model="valid">
        <v-row>
          <v-col>
            <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error">
              {{ errorMessage }}
            </v-alert>
            <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning">
              Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <draggable v-model="topoImages" item-key="url" class="v-row" @change="handleChangeImages">
            <template #item="{ element: image, index: i }">
              <v-col cols="4">
                <v-card flat>
                  <v-img
                    :src="image.url"
                    class="text-white align-end"
                    gradient="to top, rgba(0,0,0,.6), rgba(0,0,0,0)"
                    max-height="300"
                    max-width="300"
                  >
                    <v-card-title class="mb-n3 ml-n2 text-body-small">{{ image.description }}</v-card-title>
                  </v-img>
                  <v-card-actions>
                    <v-text-field label="Description" v-model="image.description" class="ml-n4" clearable></v-text-field>
                    <v-btn icon color="error" @click="removeImage(i)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </template>
          </draggable>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script>
  import draggable from "vuedraggable";
  import FileDrop from "@/components/FileDrop";
  import { UploadService } from "@/store/modules/upload";
  import SectionsService from "@/store/modules/sections";
  import { notify } from "@/services/Notification.service";

  export default {
    components: {
      FileDrop,
      draggable,
    },
    props: {
      section: {
        type: Object,
        default: () => ({}),
      },
      reloadData: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        photosDropzone: [],
        uploadWaiting: false,
        topoImages: [],
        isModification: false,
        errorMessage: "",
        valid: true,
      };
    },
    async created() {
      await this.getTopoImages();
    },
    async updated() {
      if (this.reloadData) {
        await this.getTopoImages();
        this.$emit("update", false);
      }
    },
    methods: {
      async getTopoImages() {
        const topoString = await SectionsService.actions.findSectionsTopos(this.section.id);
        this.topoImages = topoString ? JSON.parse(topoString.links) : [];
        this.watchImages();
      },
      watchImages() {
        this.topoImages.forEach((val) => {
          this.$watch(() => val, this.handleChangeImages, { deep: true });
        });
      },
      handleChangeImages() {
        this.errorMessage = "";
        this.isModification = true;
      },
      async uploadPhotos() {
        this.uploadWaiting = true;
        const allOperations = this.photosDropzone.map((image) =>
          UploadService.actions.uploadImage(`${this.section.id}/topo`, image)
        );

        const allFiles = await Promise.all(allOperations).then((result) =>
          result.filter((res) => res.success).map((res) => ({ url: res.url, fileName: res.imageName }))
        );

        for (const file of allFiles) {
          this.topoImages.push({ url: file.url, fileName: file.fileName, description: "" });
        }

        await SectionsService.actions.setSectionsTopos(this.section.id, this.topoImages);
        this.watchImages();
        this.photosDropzone = [];
        this.uploadWaiting = false;
      },
      removeImage(index) {
        UploadService.actions
          .deleteImage(`${this.section.id}/topo`, this.topoImages[index].fileName)
          .then(() => this.topoImages.splice(index, 1))
          .then(() => SectionsService.actions.setSectionsTopos(this.section.id, this.topoImages));
      },
      async save() {
        if (this.valid) {
          SectionsService.actions
            .setSectionsTopos(this.section.id, this.topoImages)
            .then(() => {
              this.isModification = false;
              notify("La section a été sauvegardée avec succès.");
            })
            .catch((error) => {
              console.error(error);
              this.errorMessage =
                "Une erreur s'est produite lors de la sauvegarde des images, vérifiez les résultats et recommencez.";
            });
        } else {
          this.errorMessage = "Certains champs obligatoires ne répondent pas aux critères attendus, veuillez vérifier.";
        }
      },
    },
    watch: {
      "section.topo_info"() {
        SectionsService.actions.updateSection(this.section);
      },
      reloadData() {
        if (this.reloadData) {
          this.errorMessage = "";
          this.isModification = false;
        }
      },
    },
  };
</script>
